import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, IconButton, CircularProgress, Box, Typography, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import { pdfjs } from 'react-pdf';
import * as pdfjsLib from 'pdfjs-dist';
import { GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDFJS_OPTIONS = {
  wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/`,
};

// Optimized hook that only generates thumbnails on demand
export function usePdfThumbnails(file) {
  const [fileUrl, setFileUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const pdfDocRef = useRef(null);
  const thumbnailCacheRef = useRef({});
  const renderQueueRef = useRef(new Set());

  useEffect(() => {
    if (file) {
      const url = file instanceof File ? URL.createObjectURL(file) : file;
      setFileUrl(url);
      return () => {
        if (file instanceof File) {
          URL.revokeObjectURL(url);
        }
      };
    }
  }, [file]);

  useEffect(() => {
    if (!file) return;

    setPdfDoc(null);

    const loadPdf = async () => {
      try {
        const fileReader = new FileReader();
        const pdfData = await new Promise((resolve, reject) => {
          fileReader.onload = () => resolve(new Uint8Array(fileReader.result));
          fileReader.onerror = reject;
          if (file instanceof File) fileReader.readAsArrayBuffer(file);
        });

        const doc = await pdfjsLib.getDocument({ data: pdfData, ...PDFJS_OPTIONS }).promise;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        setPdfDoc(doc);
      } catch (error) {
        console.error('Error loading PDF:', error);
      }
    };

    loadPdf();

    return () => {
      thumbnailCacheRef.current = {};
      renderQueueRef.current = new Set();
    };
  }, [file]);

  const generateThumbnail = async (pageNum) => {
    if (!pdfDocRef.current) return null;

    if (thumbnailCacheRef.current[pageNum]) {
      return thumbnailCacheRef.current[pageNum];
    }

    if (renderQueueRef.current.has(pageNum)) {
      return null;
    }

    renderQueueRef.current.add(pageNum);

    try {
      const pdf = pdfDocRef.current;

      if (pageNum > 0 && pageNum <= pdf.numPages) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1 });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', {
          alpha: false,
          willReadFrequently: false
        });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport,
          background: 'white'
        }).promise;

        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);

        thumbnailCacheRef.current[pageNum] = dataUrl;
        page.cleanup();

        return dataUrl;
      }
    } catch (err) {
      console.error(`Error rendering page ${pageNum}:`, err);
    } finally {
      renderQueueRef.current.delete(pageNum);
    }

    return null;
  };

  return { fileUrl, numPages, generateThumbnail, pdfDoc };
}

// Custom hook for intersection observer with thumbnail loading
export function useThumbnailLoader(pageNum, generateThumbnail) {
  const [thumbnail, setThumbnail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const observerRef = useRef(null);
  const elementRef = useRef(null);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '300px',
      threshold: 0
    };

    const handleIntersection = async (entries) => {
      const [entry] = entries;

      if (entry.isIntersecting && !thumbnail && !isLoading) {
        setIsLoading(true);
        const thumb = await generateThumbnail(pageNum);
        if (thumb) {
          setThumbnail(thumb);
        }
        setIsLoading(false);
      }
    };

    observerRef.current = new IntersectionObserver(handleIntersection, options);

    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [pageNum, generateThumbnail, thumbnail, isLoading]);

  return { elementRef, thumbnail, isLoading };
}

export function PdfPageDialog({
  open,
  onClose,
  fileUrl,
  pdfDoc,
  pageNumber: initialPageNumber = 1,
  fileName,
  totalPages
}) {
  const [currentPage, setCurrentPage] = useState(initialPageNumber);
  const [numPages, setNumPages] = useState(totalPages || 0);
  const [scale, setScale] = useState(1.5);
  const [isRendering, setIsRendering] = useState(false);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    setCurrentPage(initialPageNumber);
  }, [initialPageNumber]);

  useEffect(() => {
    if (pdfDoc) setNumPages(pdfDoc.numPages);
  }, [pdfDoc]);

  // Render the current page onto the canvas whenever page, scale, or doc changes
  useEffect(() => {
    if (!pdfDoc || !open) return;

    let cancelled = false;

    const renderPage = async () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      setIsRendering(true);

      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;

        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d', { alpha: false });
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderTask = page.render({ canvasContext: context, viewport, background: 'white' });
        renderTaskRef.current = renderTask;

        await renderTask.promise;
        page.cleanup();
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('Error rendering page:', err);
        }
      } finally {
        renderTaskRef.current = null;
        if (!cancelled) setIsRendering(false);
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, scale, open]);

  const handlePrevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(numPages, prev + 1));
  }, [numPages]);

  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(3, prev + 0.25));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(0.5, prev - 0.25));
  }, []);

  const handleDownload = () => {
    if (!fileUrl) return;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') handlePrevPage();
      else if (e.key === 'ArrowRight') handleNextPage();
      else if (e.key === '+' || e.key === '=') handleZoomIn();
      else if (e.key === '-') handleZoomOut();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handlePrevPage, handleNextPage, handleZoomIn, handleZoomOut]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Previous page (←)">
            <span>
              <IconButton onClick={handlePrevPage} disabled={currentPage <= 1} size="small">
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center' }}>
            Page {currentPage} of {numPages}
          </Typography>

          <Tooltip title="Next page (→)">
            <span>
              <IconButton onClick={handleNextPage} disabled={currentPage >= numPages} size="small">
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Zoom out (-)">
            <IconButton onClick={handleZoomOut} disabled={scale <= 0.5} size="small">
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>

          <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Typography>

          <Tooltip title="Zoom in (+)">
            <IconButton onClick={handleZoomIn} disabled={scale >= 3} size="small">
              <ZoomInIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Download PDF">
            <IconButton onClick={handleDownload} size="small" disabled={!fileUrl}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Close (Esc)">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <DialogContent
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 3,
          overflow: 'auto',
          bgcolor: '#f5f5f5'
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          {isRendering && (
            <Box sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.6)',
              zIndex: 1,
            }}>
              <CircularProgress />
            </Box>
          )}
          <canvas ref={canvasRef} style={{ display: 'block', maxWidth: '100%' }} />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
